import { Component, OnDestroy, OnInit } from '@angular/core'
import { CommonModule } from '@angular/common'
import { WeatherWidget } from 'htv-lib'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import moment from 'moment'
import { QuadrigaWeatherService, WeatherData } from '../../services/quadriga-weather.service'
import { TimeFormatPipe } from '../../pipes/time-format.pipe'
import { TemperaturePipe } from '../../pipes/temperature.pipe'
import { DayNamePipe } from '../../pipes/day-name.pipe'
import { BaseWidgetComponent } from '../base-widget/base-widget.component'

interface FormattedForecast {
  highTemperature: string
  lowTemperature: string
  humidity: string
  sunrise: string
  sunset: string
  weathercode: string
  icon: string
  condition: string
}

interface FormattedWeatherData {
  location: string
  forecasts: FormattedForecast[]
}

@Component({
  selector: 'app-weather',
  standalone: true,
  imports: [CommonModule, TranslateModule, TimeFormatPipe, TemperaturePipe, DayNamePipe],
  templateUrl: './weather.component.html',
  styleUrls: ['./weather.component.scss'],
})
export class WeatherComponent extends BaseWidgetComponent implements OnInit, OnDestroy {
  weatherData: WeatherData | null = null
  formattedWeatherData: FormattedWeatherData | null = null
  locale = moment.locale()

  constructor(
    private translateService: TranslateService,
    private quadrigaWeatherService: QuadrigaWeatherService
  ) {
    super()
  }

  override ngOnInit(): void {
    super.ngOnInit()
    this.loadWeatherData()
  }

  loadWeatherData(): void {
    const config = this.config as WeatherWidget
    this.subscriptions.add(
      this.quadrigaWeatherService.fetchWeatherData(config.city).subscribe(data => {
        this.weatherData = data
        if (data) {
          this.formattedWeatherData = this.formatWeatherData(data)
        }
      })
    )
  }

  formatWeatherData(data: WeatherData): FormattedWeatherData {
    return {
      location: data.location,
      forecasts: data.forecasts.map(forecast => ({
        highTemperature: forecast.highTemperature,
        lowTemperature: forecast.lowTemperature,
        humidity: forecast.humidity,
        sunrise: forecast.sunrise,
        sunset: forecast.sunset,
        weathercode: forecast.weathercode,
        icon: this.getWeatherIcon(forecast.weathercode),
        condition: this.getWeatherCondition(forecast.weathercode),
      })),
    }
  }

  isDayTime(): boolean {
    if (!this.weatherData?.forecasts[0]) return true

    const now = new Date()
    const today = this.weatherData.forecasts[0]

    try {
      const sunrise = new Date(today.sunrise)
      const sunset = new Date(today.sunset)
      return now >= sunrise && now <= sunset
    } catch {
      return true
    }
  }

  getWeatherIcon(weathercode: string): string {
    const codeMapping: { [key: string]: string } = {
      '0': '31',
      '1': '31',
      '2': '31',
      '3': '33',
      '4': '33',
      '5': '33',
      '6': '28',
      '7': '28',
      '8': '26',
      '10': '19',
      '11': '19',
      '17': '37',
      '18': '24',
      '23': '18',
      '24': '10',
      '25': '45',
      '26': '11',
      '27': '14',
      '31': '11',
      '36': '21',
      '44': '15',
      '45': '20',
      '48': '19',
      '50': '9',
      '51': '9',
      '56': '10',
      '60': '39',
      '61': '39',
      '63': '8',
      '65': '12',
      '66': '10',
      '67': '10',
      '68': '18',
      '69': '18',
      '70': '14',
      '71': '14',
      '73': '16',
      '75': '41',
      '77': '13',
      '79': '15',
      '80': '39',
      '81': '12',
      '83': '14',
      '84': '18',
      '85': '14',
      '86': '41',
      '87': '17',
      '88': '17',
      '95': '4',
      '96': '4',
      '99': '4',
    }

    const iconNumber = codeMapping[weathercode] || '31'
    const suffix = this.isDayTime() ? 'd' : 'n'
    return `assets/images/weather/${iconNumber}${suffix}.png`
  }

  getWeatherCondition(weathercode: string): string {
    const conditions: { [key: string]: string } = {
      '0': 'Clear',
      '1': 'Clear',
      '2': 'Clear',
      '3': 'Sunny',
      '4': 'Sunny',
      '5': 'Sunny',
      '6': 'Cloudy',
      '7': 'Cloudy',
      '8': 'Overcast',
      '10': 'Light Rain',
      '11': 'Light Rain',
      '17': 'Thunderstorm',
      '18': 'Windy',
      '23': 'Sleet',
      '24': 'Freezing Rain',
      '25': 'Hail',
      '26': 'Snow',
      '27': 'Blizzard',
      '31': 'Snow',
      '36': 'Hot',
      '44': 'Fog',
      '45': 'Fog',
      '48': 'Light Rain',
      '50': 'Drizzle',
      '51': 'Drizzle',
      '56': 'Freezing Rain',
      '60': 'Mostly Cloudy',
      '61': 'Mostly Cloudy',
      '63': 'Rain',
      '65': 'Heavy Rain',
      '66': 'Freezing Rain',
      '67': 'Freezing Rain',
      '68': 'Sleet',
      '69': 'Sleet',
      '70': 'Snow',
      '71': 'Snow',
      '73': 'Heavy Snow',
      '75': 'Blizzard',
      '77': 'Hail',
      '79': 'Fog',
      '80': 'Mostly Cloudy',
      '81': 'Heavy Rain',
      '83': 'Snow',
      '84': 'Sleet',
      '85': 'Snow',
      '86': 'Blizzard',
      '87': 'Thunderstorm',
      '88': 'Thunderstorm',
      '95': 'Thunderstorm',
      '96': 'Thunderstorm',
      '99': 'Thunderstorm',
    }

    const conditionKey = conditions[weathercode] || 'Unknown'
    return this.translateService.instant(`WIDGETS.WEATHER.CONDITIONS.${conditionKey}`)
  }

  override applyStyles(): void {
    super.applyStyles()

    const config = this.config as WeatherWidget
    const widgetStyle: Record<string, string | number> = {}
    const forecastColumnStyle: Record<string, string | number> = {}
    const dayHeaderStyle: Record<string, string | number> = {}

    if (config.cornerRadius !== undefined) {
      widgetStyle['border-radius'] = `${this.scaleToResolution(config.cornerRadius)}px`
      forecastColumnStyle['border-radius'] = `${this.scaleToResolution(config.cornerRadius)}px`
    }

    if (config.mainColor) {
      widgetStyle['background-color'] = config.mainColor
    }

    if (config.secondaryColor) {
      forecastColumnStyle['background-color'] = config.secondaryColor
    }

    if (config.tertiaryColor) {
      dayHeaderStyle['background-color'] = config.tertiaryColor
    }

    if (config.textColor) {
      widgetStyle['color'] = config.textColor
    }

    this.setRule('.weather-widget', widgetStyle)
    this.setRule('.forecast-column', forecastColumnStyle)
    this.setRule('.day-header', dayHeaderStyle)
  }
}
